import React, { useState, useEffect } from 'react';
import { string } from 'prop-types';
import { mixpanel, bigQuery, stats } from '@fiverr-private/obs';
import { Icon } from '@fiverr-private/fit';
import { r_times } from '@fiverr-private/fit/icons';
import cookie from '../../../lib/cookie';
import {
    DOMAIN_NAME,
    DEFAULT_SUBDOMAIN,
    LOCALE_TO_SUBDOMAIN_MAP
} from '../../constants';
import {
    DISCOVER_FIVERR_COOKIES,
    COOKIE_EXPIRES,
    EVENT_TYPES, EVENTS
} from './constants';
import './style.scss';

/**
 * Returns a redirect url from a given browser language based on the current url.
 * @param {String} url The current request url.
 * @param {String} browserLanguage The current request detected browser language.
 * @return {String}
 */
export const getRedirectUrl = (url, browserLanguage) => {
    const current = new URL(url);
    const subdomain = LOCALE_TO_SUBDOMAIN_MAP[browserLanguage];

    const isDefault = subdomain === DEFAULT_SUBDOMAIN;

    current.host = [
        !isDefault && subdomain,
        DOMAIN_NAME
    ].filter(Boolean).join('.');

    return current.toString();
};

/**
 * Tracks a given event to all related tracking platforms.
 * @param {String} type The event that occurred.
 * @param {String} browserLanguage The current detected browser language.
 */
const trackEvent = (type, browserLanguage) => {
    const trackingPlatforms = EVENTS[type];
    const shortLanguage = browserLanguage
        .substr(0, 2)
        .toLowerCase();

    bigQuery.send({
        type: trackingPlatforms.bigQuery,
        user: {
            locale: browserLanguage,
            id: null
        },
        domain: {
            name: 'Localization'
        },
        url: window.location.href
    });

    mixpanel.track(trackingPlatforms.mixpanel, { browserLanguage: shortLanguage });

    stats.count('localization', `${trackingPlatforms.grafana}.${shortLanguage}`);
};

/**
 * Sets a related discover fiverr cookie.
 */
const setCookie = (name, value) => cookie.set(name, value, COOKIE_EXPIRES);

const DiscoverFiverr = ({ url, browserLanguage, introText, letsGoText }) => {
    const [show, setShow] = useState(true);

    const onDismiss = () => {
        setCookie(DISCOVER_FIVERR_COOKIES.DISMISS, true);

        trackEvent(EVENT_TYPES.DISMISS, browserLanguage);

        setShow(false);
    };

    const onDiscoverClick = () => {
        setCookie(DISCOVER_FIVERR_COOKIES.GUEST_LOCALE, browserLanguage);

        trackEvent(EVENT_TYPES.CLICK, browserLanguage);

        window.location = getRedirectUrl(url, browserLanguage);
    };

    useEffect(() => {
        trackEvent(EVENT_TYPES.SHOW, browserLanguage);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return show ? (
        <div className="discover-fiverr flex-center">
            <p>
                {introText}
            </p>
            <span className="discover-link" onClick={onDiscoverClick}>
                {letsGoText}
            </span>
            <Icon className="dismiss" size={11} onClick={onDismiss}>{r_times}</Icon>
        </div>
    ) : null;
};

DiscoverFiverr.propTypes = {
    browserLanguage: string,
    url: string,
    introText: string.isRequired,
    letsGoText: string.isRequired
};

export * from './show';

export default DiscoverFiverr;
